import { DEG } from './math';

/**
 * The sun: the one forcing every region shares and no region can change.
 *
 * Light drives production (Phase 2), day length cues migration (Phase 6), and
 * the hemispheres' seasons are this file's sign. Phase 0 computes it for every
 * region and every sanctuary every day; Phase 1 turns it into light at the
 * surface and light at depth.
 */

/**
 * The solar declination at an instant, degrees. `epochDay` is days since
 * 1970-01-01 00:00 UTC and may be fractional; the tick reads it at noon UTC
 * (`noonUtc`).
 *
 * **Meeus's low-precision sun**, as NOAA's Global Monitoring Laboratory solar
 * calculator implements it: the sun's geometric mean longitude and mean
 * anomaly in Julian centuries since J2000.0, the equation of the center, the
 * apparent longitude with the nutation term, and the true obliquity of the
 * ecliptic (Meeus, J., *Astronomical Algorithms*, 2nd ed. 1998, ch. 25). Good
 * to about a hundredth of a degree over the centuries a game can span.
 *
 * **Why not Spencer's series, which this used until M12.** Spencer (1971) is a
 * fit in the day of the year alone, and the calendar's equinoxes have moved
 * since it was fitted: MEASURED at M12, it puts the 2026 March equinox between
 * 21 and 22 March and the June solstice on 22 June, against the US Naval
 * Observatory's 20 March 14:46 UTC and 21 June 08:24 UTC. On the old 360-day
 * calendar the error was folded into the calendar's own; on the real one it is
 * a day of error in the sun. `tests/astronomy.test.ts` holds this function to
 * the Observatory's sixteen equinoxes and solstices of 2024 to 2027.
 */
export function solarDeclinationDeg(epochDay: number): number {
  const julianDay = epochDay + 2440587.5;
  const t = (julianDay - 2451545) / 36525;
  const meanLongitude = 280.46646 + t * (36000.76983 + t * 0.0003032);
  const meanAnomaly = (357.52911 + t * (35999.05029 - 0.0001537 * t)) * DEG;
  const center = Math.sin(meanAnomaly) * (1.914602 - t * (0.004817 + 0.000014 * t))
    + Math.sin(2 * meanAnomaly) * (0.019993 - 0.000101 * t)
    + Math.sin(3 * meanAnomaly) * 0.000289;
  const omega = (125.04 - 1934.136 * t) * DEG;
  const apparentLongitude = (meanLongitude + center - 0.00569 - 0.00478 * Math.sin(omega)) * DEG;
  const meanObliquity = 23 + (26 + (21.448 - t * (46.815 + t * (0.00059 - t * 0.001813))) / 60) / 60;
  const obliquity = (meanObliquity + 0.00256 * Math.cos(omega)) * DEG;
  return Math.asin(Math.sin(obliquity) * Math.sin(apparentLongitude)) / DEG;
}

/**
 * The instant the tick reads the sun at: noon UTC on a date. One sun for every
 * place on a day; the declination moves at most about 0.4° a day, so a place
 * ten hours of longitude from Greenwich reads it within about 0.17° of its own
 * noon, under a minute of day length at Maui Nui's latitude.
 */
export function noonUtc(epochDay: number): number {
  return Math.floor(epochDay) + 0.5;
}

/**
 * The sun's altitude at sunrise and sunset, degrees: its upper limb on the
 * horizon, with standard atmospheric refraction. −0.833° is the almanac
 * convention (about 0.57° of refraction and 0.27° of half the disk), so a day
 * length here is the one a sunrise table prints, and at the equator it is
 * about seven minutes longer than twelve hours, not twelve exactly.
 */
const SUNRISE_ALTITUDE_DEG = -0.833;

/** Hours from sunrise to sunset at a latitude, given the declination. 24 in polar day, 0 in polar night. */
export function dayLengthHours(latDeg: number, declinationDeg: number): number {
  const phi = latDeg * DEG;
  const delta = declinationDeg * DEG;
  const cosH = (Math.sin(SUNRISE_ALTITUDE_DEG * DEG) - Math.sin(phi) * Math.sin(delta))
    / (Math.cos(phi) * Math.cos(delta));
  if (cosH <= -1) return 24;
  if (cosH >= 1) return 0;
  return (2 * Math.acos(cosH)) / DEG / 15;
}

/** The sun's elevation at local noon, degrees; negative when it does not rise. */
export function noonElevationDeg(latDeg: number, declinationDeg: number): number {
  return 90 - Math.abs(latDeg - declinationDeg);
}

/**
 * The mean lunation (M68): days from new moon to new moon, and a mean new
 * moon, epoch days. Meeus's mean new moon, the first two terms of its
 * series (Meeus, J., *Astronomical Algorithms*, 2nd ed. 1998, ch. 49: JDE =
 * 2451550.09766 + 29.530588861 k, k whole), 6 January 2000 at 14:20 TT. The
 * true new moon wanders from the mean by the moon's and the sun's uneven
 * motions: MEASURED against PyEphem 4.2.1's 2,474 new moons from 1950 to
 * 2150 (a run in a scratch copy, not kept: the repository carries no
 * ephemeris; `tests/astronomy.test.ts` holds 2026's twelve), the mean is
 * within 0.59 days of each, a standard deviation of 0.31 days (the minute
 * or so between TT and UT aside: about half a minute in 1950, a minute
 * today, a few minutes projected by 2150, nothing at 0.59 days).
 */
export const SYNODIC_MONTH_DAYS = 29.530588861;
const MEAN_NEW_MOON_EPOCH_DAY = 2451550.09766 - 2440587.5;

/** The moon's age at an instant (epoch days, fractional): days since the mean new moon before it. */
export function moonAgeDays(epochDay: number): number {
  const age = (epochDay - MEAN_NEW_MOON_EPOCH_DAY) % SYNODIC_MONTH_DAYS;
  return age < 0 ? age + SYNODIC_MONTH_DAYS : age;
}
